import colors from '@mathflat/common/style/colors'
import clsx from 'clsx'
import { format, isValid } from 'date-fns'
import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isTablet } from 'react-device-detect'

import { Icon } from '@/component/atoms/Icon'
import Calendar from '@/component/molecules/Calendar'
import { Portal } from '@/component/molecules/Portal'
import { DATE_PICKER_COMPONENT, PORTAL_DEFAULT_ID } from '@/constant/ui/DOM'
import useScrollbars from '@/hooks/useScrollbars'
import type { StyleProps } from '@/types'
import type { DateStr } from '@/types/common/commonTypes'

import DatePeriodPicker from './DatePeriodPicker'
import S from './DatePicker.style'
import RangePeriodPicker from './RangePeriodPicker'

export type DatePickerProps = {
  date: Date | null
  setDate: (date: Date) => void
  minDate?: Date
  maxDate?: Date
  disabled?: boolean
  multiSelectYear?: number[]
  position?: 'left' | 'right' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'portal'
  footer?: React.ReactNode
  pointedDates?: Set<DateStr<'-'>>
  onPrevMonthClick?: ({ year, month }: { year: number; month: number }) => void
  onNextMonthClick?: ({ year, month }: { year: number; month: number }) => void
  onOpenCalendar?: () => void
  onCloseCalendar?: () => void
  options?: {
    parentScrollbarSelector?: string // parent가 Scrollbars일 때 onScroll 이벤트를 listen하지 못해서 추가해주는 옵션
  }
  scrollIntoView?: boolean
  placeholder?: string
} & StyleProps

const DatePicker = ({
  className,
  date,
  setDate,
  minDate,
  maxDate,
  disabled,
  position = 'left',
  footer,
  pointedDates,
  multiSelectYear,
  onPrevMonthClick,
  onNextMonthClick,
  onOpenCalendar,
  onCloseCalendar,
  options,
  scrollIntoView,
  placeholder = 'YYYY.MM.DD',
}: DatePickerProps) => {
  const [text, setText] = useState(date && isValid(date) ? format(date, 'yyyy.MM.dd') : undefined)
  const [pickerPosition, setPickerPosition] =
    useState<Pick<CSSProperties, 'top' | 'bottom' | 'left' | 'right'>>()
  const [hasError, setHasError] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const isShownCalendar = () => calendarRef.current?.classList.contains('visible')

  const setIsShownCalendar = (visible: boolean) => {
    if (visible) {
      calendarRef.current?.classList.add('visible')
    } else {
      calendarRef.current?.classList.remove('visible')
    }
  }

  const { el: scrollEl } = useScrollbars(
    // options parentScrollbarSelector에는 어디서 onScroll을 해줄지, 클래스명으로 selector를 선언하여 사용
    // ex) 전달해줄때, options={{ parentScrollbarSelector: '.step3-scroll-bar > div:first-of-type' }}
    options?.parentScrollbarSelector,
    undefined,
    [],
  )

  const handleClickDate = useCallback(
    (date: Date) => {
      setDate(date)
      setText(format(date, 'yyyy.MM.dd'))
      setHasError(false)
      // document.body.click() // 다른 캘린더 닫히게 하는 코드였으나 portal이 겹치게 되면서 사이드 이펙트 발생
      setIsShownCalendar(false)
      onCloseCalendar?.()
    },
    [setDate],
  )

  const calcPosition = () => {
    if (!ref.current) {
      return
    }

    const { top } = ref.current.getBoundingClientRect()
    const { clientWidth, clientHeight } = ref.current

    // portal 사용시 (현재 portal옵션의 position은 datePicker의 바로 오른쪽에 위치하도록 설정)
    if (position === 'portal') {
      const rect = ref.current?.getBoundingClientRect()
      if (top > window.innerHeight - 170) {
        return {
          bottom: 0,
          left: rect.left + rect.width + 1,
        }
      } else {
        return {
          top: rect.top - 170 - rect.height / 2,
          left: rect.left + rect.width + 1,
        }
      }
    }
    // portal 사용 안할 시
    else {
      if (position === 'bottomLeft') {
        return {
          // margin size = 6
          top: clientHeight + 6,
          left: -1,
        }
      } else if (position === 'bottomRight') {
        return {
          top: clientHeight + 6,
          right: -1,
        }
      } else if (position === 'top') {
        return {
          top: -(clientHeight / 2 + 340 + 6),
          right: -1,
        }
      } else if (position === 'topLeft') {
        return {
          // 캘린더 높이와 무관하게 필드 바로 위에 붙인다
          bottom: clientHeight + 4,
          left: -1,
        }
      }

      if (top > window.innerHeight / 2) {
        return {
          // -Calendar 높이 + DatePicker 높이 / 2
          top: -170 + clientHeight / 2,
          [position]: clientWidth + 5,
        }
      } else {
        return {
          top: clientHeight + 5,
          [position]: -1,
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const closeCalendar = (e: any) => {
    const prevOpenedCalenderElements = document.querySelectorAll(
      `.${DATE_PICKER_COMPONENT}.visible`,
    )

    for (const el of prevOpenedCalenderElements) {
      if (!el.contains(e.target)) {
        el.classList.remove('visible')
      }
    }
    onCloseCalendar?.()
  }

  const observer = useRef<MutationObserver>(
    new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        const portalEl = document.querySelector(`#${PORTAL_DEFAULT_ID}`)

        // not visible -> visible
        if (
          !mutation.oldValue?.includes('visible') &&
          (mutation.target as Element).className.includes('visible')
        ) {
          document.addEventListener('click', closeCalendar)
          portalEl?.addEventListener('click', closeCalendar)
          return
        }

        // visible -> not visible
        if (
          mutation.oldValue?.includes('visible') &&
          !(mutation.target as Element).className.includes('visible')
        ) {
          document.removeEventListener('click', closeCalendar)
          portalEl?.removeEventListener('click', closeCalendar)
        }
      }
    }),
  )

  const unobserve = () => {
    const portalEl = document.querySelector(`#${PORTAL_DEFAULT_ID}`)
    document.removeEventListener('click', closeCalendar)
    portalEl?.removeEventListener('click', closeCalendar)
    observer.current.disconnect()
  }

  useEffect(() => {
    return () => {
      unobserve()
    }
  }, [])

  useEffect(() => {
    if (date === null || !isValid(date)) {
      setText('')
      return
    }
    setText(format(date, 'yyyy.MM.dd'))
  }, [date])

  useEffect(() => {
    // portal에서 열 때 ref.current값 타이밍 이슈 생겨서 setState로 position 세팅
    if (ref.current) {
      setPickerPosition(calcPosition())
    }
  }, [ref.current])

  const RenderCalendar = () => (
    <div className={clsx(DATE_PICKER_COMPONENT, isShownCalendar() && 'visible')} ref={calendarRef}>
      <Calendar
        css={hasError && { marginTop: 20 }}
        onDateSelect={handleClickDate}
        selectedDate={date ?? undefined}
        minDate={minDate}
        maxDate={maxDate}
        style={{
          position: 'absolute',
          zIndex: 100,
          ...pickerPosition,
        }}
        multiSelectYear={multiSelectYear}
        pointedDates={pointedDates}
        onPrevMonthClick={onPrevMonthClick}
        onNextMonthClick={onNextMonthClick}
        footer={footer}
        scrollIntoView={scrollIntoView}
      />
    </div>
  )

  return (
    <S.Wrapper
      className={clsx(
        className,
        disabled && 'disabled',
        isShownCalendar() && 'focused',
        hasError && 'error',
      )}
      onClick={(e) => {
        e.stopPropagation()
        if (disabled) return

        if (!isShownCalendar()) {
          if (calendarRef.current) {
            observer.current.observe(calendarRef.current, {
              attributes: true,
              attributeFilter: ['class'],
              attributeOldValue: true,
            })
          }
          scrollEl?.addEventListener('scroll', closeCalendar, { once: true })
        } else {
          scrollEl?.removeEventListener('scroll', closeCalendar)
        }
        const prevOpenedCalenderElement = document.querySelector(
          `.${DATE_PICKER_COMPONENT}.visible`,
        )

        if (prevOpenedCalenderElement !== calendarRef.current) {
          closeCalendar(e)
        }

        setIsShownCalendar(!isShownCalendar())
        onOpenCalendar?.()
      }}
      tabIndex={disabled ? undefined : 0}
      ref={ref}
    >
      <Icon
        css={{ marginRight: 8 }}
        name="calendar"
        size={20}
        color={disabled || date === null ? colors.gray.$400 : colors.gray.$600}
      />
      <input
        css={{
          width: '100%',
          border: 'none',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: disabled ? colors.gray.$400 : colors.gray.$900,
        }}
        type="text"
        value={text}
        inputMode="none"
        onChange={(e) => {
          e.stopPropagation()
          if (isTablet) {
            setText(text ?? '')
            return
          }

          const _text = (() => {
            if (e.target.value.length === 5) {
              if (e.target.value.includes('.')) {
                return e.target.value.replace('.', '')
              }

              return e.target.value.slice(0, 4) + '.' + e.target.value[e.target.value.length - 1]
            }

            if (e.target.value.length === 8) {
              if (e.target.value.includes('.', 5)) {
                return e.target.value
                  .split('')
                  .reverse()
                  .join('')
                  .replace('.', '')
                  .split('')
                  .reverse()
                  .join('')
              }

              return e.target.value.slice(0, 7) + '.' + e.target.value[e.target.value.length - 1]
            }

            return e.target.value
          })()

          setText(_text)
          if (e.target.value.length === 10) {
            const newDate = new Date(_text)

            if (isValid(newDate)) {
              setDate(newDate)
              setHasError(false)
            } else {
              setHasError(true)
            }
          }
        }}
        // 자릿수를 다 채우지 않았거나 잘못된 날짜면 값이 바뀌지 않으므로, 입력창도 실제 값으로 되돌린다
        onBlur={() => {
          setText(date && isValid(date) ? format(date, 'yyyy.MM.dd') : '')
          setHasError(false)
        }}
        placeholder={placeholder}
        maxLength={10}
        disabled={disabled}
      />
      {hasError && <p className="error-message">잘못된 날짜입니다.</p>}
      {position === 'portal' ? <Portal>{RenderCalendar()}</Portal> : RenderCalendar()}
    </S.Wrapper>
  )
}

DatePicker.Period = DatePeriodPicker
DatePicker.RangePeriod = RangePeriodPicker

export default observer(DatePicker)
